import serviceWorkerEvents from './service-worker-events';

/**
 * @description We need to normalize path because on dev - it would be /, but on
 * sandbox it usually /s/SiteId/
 * @param relativeUrl - relative url to service worker
 * @param includeDomain - if true, domain will be included in the url
 * @returns normalized url to service worker
 */
function normalizeUrl(relativeUrl: string, includeDomain = false): string {
    const sandboxUrlType = window.location.pathname.match(/(\/s\/[^/]+)\/?/);

    let url = (sandboxUrlType ? sandboxUrlType[1] : '') + relativeUrl;

    if (includeDomain) {
        url = window.location.origin + url;
    }

    return url;
}

/**
 * @description Check if service worker is old
 * @param registration - service worker registration
 * @returns true if service worker is old or false if not
 */
function isOldServiceWorker(registration: ServiceWorkerRegistration) {
    if (!registration.scope.startsWith(window.location.origin)) {
        // Skip service workers that are not from the same origin
        return false;
    }

    if (registration.active?.scriptURL === normalizeUrl('/service-worker.js', true)) {
        // Skip the current service worker
        return false;
    }

    return true;
}

/**
 * @description Remove old service workers
 * @returns Promise that resolves when all old service workers are removed
 */
function removeOldServiceWorkers() {
    return navigator.serviceWorker.getRegistrations().then((registrations) => {
        const unregisterPromises: Array<Promise<unknown>> = [];

        for (let i = 0; i < registrations.length; i++) {
            const registration = registrations[i];

            if (isOldServiceWorker(registration)) {
                unregisterPromises.push(registration.unregister());
            }
        }

        return Promise.all(unregisterPromises);
    });
}

/**
 * @description Register service worker
 */
function registerServiceWorker() {
    let serviceWorkerInstance;

    navigator.serviceWorker.register(normalizeUrl('/service-worker.js'))
        .then((registration) => {
            serviceWorkerInstance = registration;
            serviceWorkerEvents.init();
        }).catch(() => {
            if (!serviceWorkerInstance) {
                return;
            }

            serviceWorkerInstance.unregister().then(isUnregistered => {
                if (isUnregistered) {
                    serviceWorkerInstance = null;
                }
            });
        });
}

/**
 * @description Service worker registration
 */
export default function () {
    if ('serviceWorker' in navigator) {
        if (window.location.pathname.indexOf('on/demandware.store') > -1) {
            // Skip service worker registration for not user friendly URLs
            return;
        }

        removeOldServiceWorkers().then(registerServiceWorker);
    }
}
